import React, { createContext, useLayoutEffect, useRef, useState } from 'react';
import { MAX_ARCHIVE_PART_BYTES } from '../../services/ownerCameraArchiveApi';
import './Camera.css';

export const OwnerCameraArchiveContext = createContext(null);

export function archiveDateGroups(sessions, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const groups = new Map();
  for (const session of sessions) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(session.startedAt)).map(p => [p.type, p.value]));
    const key = `${parts.year}-${parts.month}-${parts.day}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(session);
  }
  return [...groups].sort(([a], [b]) => b.localeCompare(a));
}
export function archiveStatus(session) {
  if (session.recording.storage === 'uploading') return 'Uploading to S3 — pending verification';
  if (session.recording.storage === 'saved' && session.recording.state === 'complete') return 'Saved to S3';
  if (session.state === 'finalizing' || session.state === 'ending') return 'Finalizing';
  if (session.state === 'live') return 'Live — recording pending';
  if (session.state === 'ready') return 'Recording pending';
  if (session.state === 'failed') return 'Broadcast failed';
  return { complete: 'Recording ready', pending: 'Recording pending', empty: 'No recording', failed: 'Recording failed', partial: 'Partial recording — playback unavailable' }[session.recording.state];
}
function RecordingVideo({ url, onError }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const video = ref.current;
    video.src = url;
    return () => { video.pause(); video.removeAttribute('src'); video.load(); };
  }, [url]);
  return <video key={url} ref={ref} src={url} controls playsInline preload="metadata" onError={onError} className="camera-archive-video" aria-label="Recording playback" />;
}
function ArchiveContents({ archive, timeZone }) {
  const { state } = archive;
  const [confirmId, setConfirmId] = useState(null);
  const [filter, setFilter] = useState('');
  const dialog = useRef(null);
  useLayoutEffect(() => {
    const previous = document.activeElement;
    dialog.current?.querySelector('button')?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  const keyboard = event => {
    if (event.key === 'Escape') { event.stopPropagation(); archive.close(); }
    if (event.key !== 'Tab') return;
    const controls = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), video[controls]')];
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const groups = archiveDateGroups(state.sessions, timeZone);
  const selected = state.sessions.find(s => s.id === state.selectedId);
  const time = value => new Intl.DateTimeFormat(undefined, { timeZone, hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short' }).format(new Date(value));
  return (
    <div className="camera-archive-backdrop">
      <section ref={dialog} onKeyDown={keyboard} className="camera-panel camera-archive" role="dialog" aria-modal="true" aria-label="My recordings">
        <header className="camera-panel-header"><h2>My recordings</h2><button type="button" className="camera-close-btn" onClick={archive.close}>Close recordings</button></header>
        <p>Only your broadcasts appear here. Dates and times are shown in <strong>{timeZone}</strong>.</p>
        <label className="camera-archive-filter">Date in loaded history <input type="date" value={filter} onChange={e => setFilter(e.target.value)} /></label>
        {filter && <button type="button" onClick={() => setFilter('')}>Show all loaded dates</button>}
        <p className="camera-panel-note">Dates are filtered locally. Load more to include earlier broadcasts; an unloaded date may have recordings.</p>
        {state.loading && <p role="status">Loading recordings…</p>}
        {state.error && <p role="alert">{state.error} <button type="button" onClick={() => archive.load(state.retryMore)}>Retry history</button></p>}
        {!state.loading && state.loaded && !state.sessions.length && <p>No broadcasts in your history.</p>}
        {filter && !groups.some(([day]) => day === filter) && <p>No sessions on this date in the loaded history.</p>}
        {groups.filter(([day]) => !filter || filter === day).map(([day, rows]) => (
          <section key={day} aria-label={day} className="camera-archive-day"><h3>{day}</h3>
            {rows.map(row => {
              const deletion = state.deletions[row.id];
              return <article key={row.id} className="camera-archive-row" data-session-id={row.id}>
                <div><strong>{time(row.startedAt)}</strong><p>{deletion ? ({ sending: 'Deletion requested — outcome pending', unknown: 'Deletion outcome unknown. Retry this exact recording.', pending: 'Hidden — Erasure pending', complete: 'Deleted — Erasure complete' }[deletion]) : archiveStatus(row)}</p></div>
                {deletion && <small>Recording ID: {row.id}</small>}
                {!deletion && <div className="camera-panel-actions">
                  <button type="button" className="camera-secondary-btn" aria-pressed={state.selectedId === row.id} onClick={() => archive.select(row.id)}>View recording</button>
                  {['ended', 'failed'].includes(row.state) && <button type="button" className="camera-danger-btn" onClick={() => setConfirmId(row.id)}>Delete recording</button>}
                </div>}
                {['unknown', 'pending'].includes(deletion) && <button type="button" className="camera-danger-btn" onClick={() => archive.remove(row.id)}>Retry deletion</button>}
                {confirmId === row.id && !deletion && <div className="camera-delete-confirm" role="alertdialog" aria-label="Confirm recording deletion">
                  <p>Delete the recording from {day} at {time(row.startedAt)}? Playback will be withheld immediately. Erasure may take longer.</p>
                  <button type="button" className="camera-danger-btn" onClick={() => { setConfirmId(null); void archive.remove(row.id); }}>Confirm delete</button>
                  <button type="button" className="camera-secondary-btn" onClick={() => setConfirmId(null)}>Keep recording</button>
                </div>}
              </article>;
            })}
          </section>
        ))}
        {state.nextCursor && <button type="button" className="camera-secondary-btn" disabled={state.loading} onClick={() => archive.load(true)}>Load more</button>}
        {state.loaded && !state.nextCursor && <p className="camera-panel-note">End of this loaded history. <button type="button" disabled={state.loading} onClick={() => archive.load(false)}>Refresh history</button></p>}
        {selected && !state.deletions[selected.id] && <section className="camera-archive-player" aria-label="Selected recording">
          <h3>{time(selected.startedAt)}</h3><p>{archiveStatus(selected)}</p>
          {state.mediaStatus === 'loading' && <p role="status">Loading recording…</p>}
          {state.mediaStatus === 'unavailable' && <p>Playback is unavailable until this recording is complete and verified.</p>}
          {state.mediaStatus === 'error' && <p role="alert">Playback could not be verified or played. <button type="button" onClick={() => archive.select(selected.id)}>Retry playback</button></p>}
          <div className="camera-archive-parts">{state.parts.map((part, index) => <button key={part.name} type="button" className="camera-secondary-btn" disabled={part.bytes > MAX_ARCHIVE_PART_BYTES} aria-pressed={state.partName === part.name} onClick={() => archive.play(part.name)}>Part {index + 1} · {(part.bytes / 1048576).toFixed(1)} MiB{part.bytes > MAX_ARCHIVE_PART_BYTES ? ' — exceeds playback limit' : ''}</button>)}</div>
          {state.url && <RecordingVideo url={state.url} onError={archive.playbackError} />}
          <p className="camera-panel-note">Playback downloads one verified part at a time, up to 64 MiB, before playing. Closing or leaving clears this player. Bytes already downloaded cannot be revoked retroactively.</p>
        </section>}
      </section>
    </div>
  );
}
export default function OwnerCameraArchive({ archive, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone }) {
  return archive.state.open ? <ArchiveContents archive={archive} timeZone={timeZone} /> : null;
}
